import React, { useState, useEffect, useMemo } from 'react';
import VirtualizedDropdown, { VirtualizedDropdownOption } from './VirtualizedDropdown';
import { API_ENDPOINTS } from '../../config/api.config';
import { getAuthHeaders } from '../../../../../../extensions/radiology-report/src/utils/dicomUtils';

interface BodyPartDropdownProps {
  modality?: string;
  value?: number | null;
  onChange: (bodyPartId: number | null, bodyPartName: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  noneLabel?: string;
  showNone?: boolean;
  containerClassName?: string;
  onSearchChange?: (search: string) => void;
  variant?: 'dark' | 'light';
  searchPlaceholder?: string;
  mutedDisplayLabels?: string[];
  /**
   * When true (default), moving the mouse over options does not scroll the list; only ArrowUp/Down does.
   * Slower wheel defaults apply — only pass overrides if you need different behavior.
   */
  onlyKeyboardSyncsListScroll?: boolean;
  /** Passed when nativeOptionsWheelScroll is false; lower = slower damped wheel */
  wheelScrollMultiplier?: number;
  wheelScrollMaxDeltaPx?: number;
  /**
   * When true (default), options list uses native browser wheel scrolling — same feel as
   * FormSelectVirtualized (User Management / facility filter dropdowns). Slower/smooth vs custom handler.
   */
  nativeOptionsWheelScroll?: boolean;
}

const BodyPartDropdown: React.FC<BodyPartDropdownProps> = ({
  modality,
  value,
  onChange,
  placeholder = 'Select body part...',
  disabled = false,
  className = '',
  noneLabel = 'All',
  showNone = false,
  containerClassName = '',
  onSearchChange,
  variant = 'dark',
  searchPlaceholder = 'Search...',
  mutedDisplayLabels,
  onlyKeyboardSyncsListScroll = true,
  nativeOptionsWheelScroll = true,
  wheelScrollMultiplier = 0.07,
  wheelScrollMaxDeltaPx = 5,
}) => {
  const [bodyParts, setBodyParts] = useState<
    Array<{ id: number; name: string; createdAt: string; updatedAt: string }>
  >([]);
  const [bodyPartSearch, setBodyPartSearch] = useState<string>('');
  const [isLoadingBodyParts, setIsLoadingBodyParts] = useState(false);

  // Fetch anatomy parts on mount and when modality changes (if provided)
  useEffect(() => {
    loadAnatomyParts(modality);
  }, [modality]);

  const loadAnatomyParts = async (modalityName?: string) => {
    setIsLoadingBodyParts(true);
    try {
      const params = new URLSearchParams();
      // Only append modality_name if it's provided
      if (modalityName) {
        params.append('modality_name', modalityName);
      }

      const url = params.toString()
        ? `${API_ENDPOINTS.ANATOMY_PARTS.GET}?${params.toString()}`
        : API_ENDPOINTS.ANATOMY_PARTS.GET;
      const response = await fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      });

      if (!response.ok) {
        throw new Error(`API request failed: ${response.status}`);
      }

      const apiResponse = await response.json();

      if (apiResponse.success && apiResponse.data && Array.isArray(apiResponse.data)) {
        setBodyParts(apiResponse.data);
      } else {
        setBodyParts([]);
      }
    } catch (error) {
      console.error('Error loading anatomy parts:', error);
      setBodyParts([]);
    } finally {
      setIsLoadingBodyParts(false);
    }
  };

  // Convert body parts to dropdown options with client-side search filtering
  const options: VirtualizedDropdownOption[] = useMemo(() => {
    let filteredParts = bodyParts;

    // Client-side search filtering by name
    if (bodyPartSearch.trim()) {
      const searchLower = bodyPartSearch.toLowerCase();
      filteredParts = bodyParts.filter(part => part.name.toLowerCase().includes(searchLower));
    }

    return filteredParts.map(part => ({
      id: part.id,
      label: part.name,
      value: part.id,
    }));
  }, [bodyParts, bodyPartSearch]);

  const handleChange = (bodyPartId: number | null, bodyPartName: string) => {
    onChange(bodyPartId, bodyPartName);
  };

  // Handle search change - this will trigger the API call via useEffect
  const handleSearchChange = (search: string) => {
    setBodyPartSearch(search);
    if (onSearchChange) {
      onSearchChange(search);
    }
  };

  // Determine empty message
  const emptyMessage = bodyPartSearch ? 'No body parts found' : 'No body parts available';

  return (
    <VirtualizedDropdown
      value={value}
      onChange={handleChange}
      options={options}
      placeholder={placeholder}
      disabled={disabled}
      isLoading={isLoadingBodyParts}
      className={className}
      noneLabel={noneLabel}
      showNone={showNone}
      searchPlaceholder={searchPlaceholder}
      emptyMessage={emptyMessage}
      loadingMessage="Loading..."
      containerClassName={containerClassName}
      showSearch={true}
      searchValue={bodyPartSearch}
      onSearchChange={handleSearchChange}
      variant={variant}
      mutedDisplayLabels={mutedDisplayLabels}
      onlyKeyboardSyncsListScroll={onlyKeyboardSyncsListScroll}
      nativeOptionsWheelScroll={nativeOptionsWheelScroll}
      wheelScrollMultiplier={wheelScrollMultiplier}
      wheelScrollMaxDeltaPx={wheelScrollMaxDeltaPx}
    />
  );
};

export default BodyPartDropdown;
